import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import './CampusLogin.css';

const fmt = value => value == null ? '—' : Number(value).toLocaleString();

export default function CampusLoginAdmin() {
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState([]);
  const [dailyEnabled, setDailyEnabled] = useState(false);
  const [batchSize, setBatchSize] = useState(25);
  const [afterId, setAfterId] = useState(0);
  const [bulk, setBulk] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadConfig = async () => {
    setBusy('load');
    setError('');
    try {
      const res = await api.get('/campuslogin/config');
      const data = res.data;
      setConfig(data);
      setSelected(data.selectedMailListIds || []);
      setDailyEnabled(!!data.dailyEnabled);
      setBatchSize(Math.min(100, Math.max(1, Number(data.batchSize) || 25)));
    } catch (err) {
      setError(err.response?.data?.error || 'CampusLogin configuration could not be loaded.');
    } finally { setBusy(''); }
  };
  useEffect(() => { loadConfig(); }, []);

  const saveConfig = async () => {
    setBusy('save');
    setError('');
    setNotice('');
    try {
      const res = await api.put('/campuslogin/config', {
        mailListIds: selected,
        dailyEnabled,
        batchSize: Math.min(100, Math.max(1, Number(batchSize) || 1)),
      });
      setConfig(prev => ({ ...prev, ...res.data }));
      setNotice('CampusLogin settings saved.');
    } catch (err) {
      setError(err.response?.data?.error || 'Settings were not saved. Daily sync may require a successful pilot apply.');
    } finally { setBusy(''); }
  };
  const pauseDaily = async () => {
    setBusy('pause');
    setError('');
    try {
      const res = await api.put('/campuslogin/config', {
        mailListIds: config.selectedMailListIds || [],
        dailyEnabled: false,
        batchSize: config.batchSize,
      });
      setConfig(res.data);
      setDailyEnabled(false);
      setNotice('Daily metadata sync paused. Email settings were not changed.');
    } catch (err) {
      setError(err.response?.data?.error || 'Daily sync could not be paused.');
    } finally { setBusy(''); }
  };

  const previewBulk = async () => {
    setBusy('preview');
    setError('');
    setNotice('');
    setBulk(null);
    try {
      const res = await api.post('/campuslogin/bulk/preview', {
        limit: Math.min(100, Math.max(1, Number(batchSize) || 1)),
        afterId: Math.max(0, Number(afterId) || 0),
      });
      setBulk(res.data);
      setNotice('Preview only. No student records were changed.');
    } catch (err) {
      setError(err.response?.data?.error || 'Bulk preview could not be created.');
    } finally { setBusy(''); }
  };

  const applyBulk = async () => {
    if (!bulk?.previewToken || !config?.pilotCompleted) return;
    setBusy('apply');
    setError('');
    setNotice('');
    try {
      const res = await api.post('/campuslogin/bulk/apply', { previewToken: bulk.previewToken, approved: true });
      setBulk(res.data);
      setNotice('Approved batch applied.');
      await loadConfig();
    } catch (err) {
      setError(err.response?.data?.error || 'Batch was not applied.');
    } finally { setBusy(''); }
  };

  const toggleList = (id, checked) => setSelected(current =>
    checked ? [...new Set([...current, id])] : current.filter(item => item !== id));
  const connected = !!config?.configured && !!config?.connection?.ok;
  const eligible = (config?.databases || []).filter(db => db.eligible);
  const counts = bulk?.counts;

  return (
    <section className="cl-panel" aria-label="CampusLogin administration">
      <div className="cl-panel-inner">
        <div className="cl-title-row">
          <div>
            <div className="cl-eyebrow">Admin controls</div>
            <h2 className="cl-title">CampusLogin metadata sync</h2>
            <p className="cl-copy">Discovery, list selection and bounded batch operations. Bulk actions are always staff-triggered; preview never changes student records.</p>
          </div>
          <span className={`cl-tag ${connected ? 'ok' : 'bad'}`}>{connected ? 'Connection ready' : 'Connection unavailable'}</span>
        </div>

        {error && <div className="cl-alert error" role="alert">{error}</div>}
        {notice && <div className="cl-alert">{notice}</div>}
        <div className="cl-status">
          <span><strong>Service:</strong> {config?.configured ? 'Configured' : 'Not configured'}{config?.connection?.error ? ` · ${config.connection.error}` : ''}</span>
          <span><strong>Pilot:</strong> {config?.pilotCompleted ? 'Completed' : 'Not completed'}</span>
        </div>
        <div className="cl-actions">
          <button className="btn btn-secondary btn-sm" onClick={loadConfig} disabled={!!busy}>{busy === 'load' ? 'Checking…' : 'Check connection'}</button>
          {config?.dailyEnabled && <button className="btn btn-secondary btn-sm" onClick={pauseDaily} disabled={!!busy}>{busy === 'pause' ? 'Pausing…' : 'Pause daily sync'}</button>}
        </div>

        {config && (
          <>
            <div className="cl-admin-grid">
              <div>
                <div className="cl-title" style={{ fontSize: 13, marginBottom: 8 }}>Discovered mailing lists</div>
                {eligible.length === 0 ? (
                  <div className="cl-alert">No eligible mailing lists were discovered. Refresh the connection status or review the CampusLogin configuration.</div>
                ) : (
                  <div className="cl-list-box">
                    {config.databases.map(db => (
                      <label className="cl-list-option" key={db.mailListId} style={{ opacity: db.eligible ? 1 : .5 }}>
                        <input type="checkbox" disabled={!db.eligible}
                          checked={selected.some(id => String(id) === String(db.mailListId))}
                          onChange={e => toggleList(db.mailListId, e.target.checked)} />
                        <span>{db.name}<span className="cl-list-meta">{db.type}{db.subType ? ` · ${db.subType}` : ''} · List {db.mailListId}{!db.eligible ? ' · Not eligible' : ''}</span></span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div className="cl-title" style={{ fontSize: 13, marginBottom: 8 }}>Sync policy</div>
                <label className="cl-field">Batch preview size (1–100)
                  <input type="number" min="1" max="100" value={batchSize} onChange={e => setBatchSize(e.target.value)} />
                </label>
                <label className="cl-candidate" style={{ marginTop: 10, opacity: config.pilotCompleted ? 1 : .58 }}>
                  <input type="checkbox" checked={dailyEnabled} disabled={!config.pilotCompleted}
                    onChange={e => setDailyEnabled(e.target.checked)} />
                  <span className="cl-candidate-main"><strong>Enable daily sync</strong><span className="cl-candidate-sub">{config.pilotCompleted ? 'Default off; save to update.' : 'Available after a successful single-student pilot apply.'}</span></span>
                </label>
                <div className="cl-actions">
                  <button className="btn btn-primary btn-sm" onClick={saveConfig} disabled={!!busy || selected.some(id => !eligible.some(db => String(db.mailListId) === String(id)))}>
                    {busy === 'save' ? 'Saving…' : 'Save configuration'}
                  </button>
                </div>
              </div>
            </div>

            <hr className="cl-divider" />
            <div className="cl-title" style={{ fontSize: 13 }}>Bounded batch run</div>
            <p className="cl-copy">Non-archived students only. Review the complete preview before approving an apply. Nothing runs automatically from this screen.</p>
            <div className="cl-form">
              <label className="cl-field">Limit (1–100)
                <input type="number" min="1" max="100" value={batchSize} onChange={e => setBatchSize(e.target.value)} />
              </label>
              <label className="cl-field">After student ID
                <input type="number" min="0" value={afterId} onChange={e => setAfterId(e.target.value)} />
              </label>
            </div>
            <div className="cl-actions">
              <button className="btn btn-secondary btn-sm" onClick={previewBulk} disabled={!!busy || !connected}>{busy === 'preview' ? 'Building preview…' : 'Preview batch'}</button>
              <button className="btn btn-primary btn-sm" onClick={applyBulk} disabled={!!busy || !bulk?.previewToken || !config.pilotCompleted}>
                {busy === 'apply' ? 'Applying…' : 'Approve & apply batch'}
              </button>
            </div>
            {!config.pilotCompleted && <div className="cl-note">Batch apply stays disabled until a successful matched single-student apply completes.</div>}
            {counts && (
              <>
                <div className="cl-kpis">
                  {['matched', 'changed', 'unmatched', 'ambiguous', 'failed'].map(key => <div className="cl-kpi" key={key}><b>{fmt(counts[key])}</b><span>{key}</span></div>)}
                </div>
                <div className="cl-result-list">
                  {(bulk.results || []).map(row => (
                    <div className="cl-result" key={row.studentId}>
                      <span><strong>{row.name || `Student ${row.studentId}`}</strong><small> · ID {row.studentId}{row.changes?.length ? ` · ${row.changes.join(', ')}` : ''}</small>{row.error && <small> · {row.error}</small>}</span>
                      {row.mapping && <small>List {row.mapping.mailListId} · Contact {row.mapping.contactId}</small>}
                      {row.evidence?.length > 0 && <small>{row.evidence.map(e => `${e.category} (document ${e.documentId})`).join(', ')}</small>}
                      <span className={`cl-tag ${row.outcome === 'matched' ? 'ok' : row.outcome === 'failed' ? 'bad' : 'warn'}`}>{row.outcome}</span>
                    </div>
                  ))}
                </div>
                <div className="cl-note">Next student cursor: {bulk.nextAfterId == null ? 'end of available students' : bulk.nextAfterId}</div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}
